import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { API } from '@trinity/chat';
import { queryKeys } from '@trinity/chat-components/query-keys';
import { ButtonSet } from '../components/Modal/ButtonSet';
import { Header } from '../components/Modal/Header';
import { Modal } from '../components/Modal/Modal';
import { Button } from '../components/presentation/Button';
import { useChatApiClient } from '../containers/Chat/useApiClient';

type QuantAnalysisItem = API.ProjectsQuantAnalysis.QuantAnalysisItem;

type UseDeleteQuantAnalysisItemModalProps = {
  analysisItem: QuantAnalysisItem;
  projectId: number | undefined;
  quantAnalysisId: number;
};

export const useDeleteQuantAnalysisItemModal = ({
  analysisItem,
  projectId,
  quantAnalysisId,
}: UseDeleteQuantAnalysisItemModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const apiClient = useChatApiClient();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      return apiClient.quantAnalysis.deleteQuantAnalysisItem({
        projectId,
        quantAnalysisId,
        itemId: analysisItem.id,
      });
    },
    onSuccess: () => {
      // Invalidate the main project query to refresh tabs and quantAnalyses data
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.one({ projectId }),
      });

      // Invalidate the main quant analysis query to refresh data
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.quantAnalysis.one({ projectId, quantAnalysisId }),
      });

      // Also invalidate versions query if needed
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.quantAnalysis.versions({ projectId, quantAnalysisId }),
      });

      setIsOpen(false);
    },
  });

  const toggleVisibility = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleDelete = useCallback(() => {
    if (projectId) {
      mutation.mutate();
    }
  }, [mutation, projectId]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const DeleteModalComponent = (
    <Modal
      open={isOpen}
      onClose={handleClose}
    >
      <Header text="Delete Analysis Item" />

      <div style={{ padding: '24px' }}>
        <div>
          Are you sure you want to delete this analysis item?
        </div>
      </div>

      <ButtonSet>
        <Button.Destructive
          onClick={handleClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button.Destructive>

        <Button.Primary
          onClick={handleDelete}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Deleting...' : 'Confirm'}
        </Button.Primary>
      </ButtonSet>
    </Modal>
  );

  return [toggleVisibility, DeleteModalComponent] as const;
};
